import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useFeatures } from "@/lib/features";
import { useWorkspace } from "@/context/WorkspaceContext";
import { playScanSound, haptic, primeScanAudio, estimateSharpness } from "@/lib/scanSound";
import WorkflowSteps from "@/components/WorkflowSteps";
import { isNative, capturePhotoFile } from "@/lib/mobile";
import CornerEditor from "@/components/scan/CornerEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Camera, UploadSimple, ArrowClockwise, Trash, ArrowUp, ArrowDown, FilePdf,
  CircleNotch, Scan, StopCircle, TextAa, ShieldStar, PenNib, CheckCircle, Crop, MagicWand,
} from "@phosphor-icons/react";

const CATEGORIES = ["Identity", "Education", "Family", "Financial", "Property", "Business", "Legal", "Insurance", "Other"];
const MODES = [["color", "Color"], ["gray", "Grayscale"], ["bw", "B&W Document"]];
const CLASSIFY_CAT = { receipt: "Financial", invoice: "Financial", contract: "Legal", agreement: "Legal", id: "Identity", passport: "Identity", license: "Identity", form: "Business", letter: "Business", "business card": "Business", statement: "Financial", insurance: "Insurance" };

// Bake a page's rotation + enhancement (brightness/contrast/mode) into a canvas.
function renderPageToCanvas(page, maxDim = 1654) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let w = img.width, h = img.height;
      const scale = Math.min(1, maxDim / Math.max(w, h));
      w = Math.round(w * scale); h = Math.round(h * scale);
      const rot = ((page.rotation % 360) + 360) % 360;
      const swap = rot === 90 || rot === 270;
      const canvas = document.createElement("canvas");
      canvas.width = swap ? h : w;
      canvas.height = swap ? w : h;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rot * Math.PI) / 180);
      const gray = page.mode === "gray" || page.mode === "bw";
      ctx.filter = `brightness(${page.brightness}%) contrast(${page.contrast}%)${gray ? " grayscale(1)" : ""}`;
      ctx.drawImage(img, -w / 2, -h / 2, w, h);
      ctx.restore();
      if (page.mode === "bw") {
        const id = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const d = id.data;
        for (let i = 0; i < d.length; i += 4) {
          const t = d[i] > 135 ? 255 : 0;
          d[i] = d[i + 1] = d[i + 2] = t;
        }
        ctx.putImageData(id, 0, 0);
      }
      resolve(canvas);
    };
    img.onerror = reject;
    img.src = page.src;
  });
}

function pageStyle(p) {
  const gray = p.mode === "gray" || p.mode === "bw";
  return {
    transform: `rotate(${p.rotation}deg)`,
    filter: `brightness(${p.brightness}%) contrast(${p.mode === "bw" ? p.contrast + 60 : p.contrast}%)${gray ? " grayscale(1)" : ""}`,
  };
}

export default function DuruScan() {
  const flags = useFeatures();
  const navigate = useNavigate();
  const { orgId } = useWorkspace();
  const [pages, setPages] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [docName, setDocName] = useState("");
  const [category, setCategory] = useState("Other");
  const [tags, setTags] = useState("");
  const [folders, setFolders] = useState([]);
  const [folderId, setFolderId] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const [cropping, setCropping] = useState(false);
  const [quota, setQuota] = useState(null);
  const [proc, setProc] = useState(null); // {stage, done:[], flash}
  const [captureFlash, setCaptureFlash] = useState(false);
  const [editCorners, setEditCorners] = useState(false);
  const [liveQuad, setLiveQuad] = useState(null); // {corners, vw, vh} live edge guidance
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const active = pages.find((p) => p.id === activeId) || null;

  const addPage = useCallback((src) => {
    const id = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setPages((prev) => [...prev, { id, src, rotation: 0, mode: "color", brightness: 100, contrast: 100 }]);
    setActiveId(id);
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setCameraOn(false);
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  // Live edge-detection guidance: sample a small frame ~1x/sec while the camera is on.
  useEffect(() => {
    if (!cameraOn) { setLiveQuad(null); return; }
    let busy = false, stopped = false;
    const tick = async () => {
      const v = videoRef.current;
      if (busy || stopped || !v || !v.videoWidth) return;
      busy = true;
      try {
        const c = document.createElement("canvas");
        const s = 480 / Math.max(v.videoWidth, v.videoHeight);
        c.width = Math.round(v.videoWidth * s); c.height = Math.round(v.videoHeight * s);
        c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
        const { data } = await api.post("/scan/detect", { image: c.toDataURL("image/jpeg", 0.6) });
        if (!stopped) setLiveQuad(data.found ? { corners: data.corners, vw: v.videoWidth, vh: v.videoHeight } : null);
      } catch { /* guidance is best-effort */ }
      finally { busy = false; }
    };
    const id = setInterval(tick, 1000);
    return () => { stopped = true; clearInterval(id); };
  }, [cameraOn]);

  const loadQuota = useCallback(() => {
    api.get("/scan/quota", { params: orgId ? { org_id: orgId } : {} }).then(({ data }) => setQuota(data)).catch(() => {});
  }, [orgId]);
  useEffect(() => { loadQuota(); }, [loadQuota]);

  const loadFolders = useCallback(() => {
    api.get("/folders", { params: orgId ? { org_id: orgId } : {} }).then(({ data }) => setFolders(data || [])).catch(() => {});
  }, [orgId]);
  useEffect(() => { loadFolders(); setFolderId(""); }, [loadFolders]);

  // Server-side Auto-enhance (deskew + shadow/glare/noise cleanup) for the active page.
  const autoEnhance = async () => {
    if (!active) return;
    setEnhancing(true);
    try {
      const { data } = await api.post("/scan/enhance", { image: active.src, mode: "auto" });
      patchPage(active.id, { src: data.image, original: active.original || active.src, mode: "color", brightness: 100, contrast: 100 });
      toast.success(data.deskew ? "Enhanced & deskewed" : "Enhanced");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Enhance failed"); }
    finally { setEnhancing(false); }
  };

  // Suggest document type + name + category from content (reuses ai_service.classify_scan).
  const suggest = async () => {
    const page = active || pages[0];
    if (!page) { toast.info("Capture a page first."); return; }
    setSuggesting(true);
    try {
      const { data } = await api.post("/scan/classify", { image: page.src });
      if (!data.ok) { toast.info("Couldn't confidently classify — enter details manually."); return; }
      if (data.suggested_name && !docName.trim()) setDocName(data.suggested_name);
      const dt = (data.document_type || "").toLowerCase();
      const mapped = CATEGORIES.includes(data.category) ? data.category : (CLASSIFY_CAT[dt] || Object.entries(CLASSIFY_CAT).find(([k]) => dt.includes(k))?.[1]) || null;
      if (mapped) setCategory(mapped);
      if (data.document_type) setTags((t) => Array.from(new Set([...(t ? t.split(",").map((x) => x.trim()) : []), "scanned", data.document_type.toLowerCase()])).join(","));
      toast.success(`Looks like a ${data.document_type || "document"}${data.status === "needs_review" ? " (please review)" : ""}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Classification failed"); }
    finally { setSuggesting(false); }
  };

  // Local OpenCV auto edge-detection + perspective correction for the active page.
  const autoCrop = async () => {
    if (!active) return;
    setCropping(true);
    try {
      const { data } = await api.post("/scan/auto-crop", { image: active.src });
      if (data.found) {
        patchPage(active.id, { src: data.image, original: active.original || active.src, rotation: 0 });
        toast.success("Document edges detected & straightened");
      } else {
        toast.info("No clear document edges found — try better lighting or crop manually");
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Auto-crop failed"); }
    finally { setCropping(false); }
  };
  const undoCrop = () => { if (active?.original) patchPage(active.id, { src: active.original, original: null }); };

  // Batch: straighten (auto-crop) and clean (B&W) every page in one tap.
  const autoCropAll = async () => {
    if (!pages.length) return;
    setCropping(true);
    let cleaned = 0;
    try {
      for (const p of pages) {
        try {
          const { data } = await api.post("/scan/auto-crop", { image: p.src });
          patchPage(p.id, { mode: "bw", contrast: 105, ...(data.found ? { src: data.image, original: p.original || p.src, rotation: 0 } : {}) });
          if (data.found) cleaned++;
        } catch { /* skip this page */ }
      }
      toast.success(`Cleaned ${pages.length} page${pages.length !== 1 ? "s" : ""}${cleaned ? `, ${cleaned} auto-cropped` : ""}`);
    } finally { setCropping(false); }
  };

  const startCamera = async () => {
    try {
      primeScanAudio();
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
      streamRef.current = stream;
      setCameraOn(true);
      playScanSound("detected");
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(() => {}); } }, 50);
    } catch {
      toast.error("Camera unavailable. You can upload images instead.");
    }
  };

  const capture = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    const src = c.toDataURL("image/jpeg", 0.92);
    playScanSound("capture"); haptic(20);
    setCaptureFlash(true); setTimeout(() => setCaptureFlash(false), 360);
    addPage(src);
    const q = await estimateSharpness(src);
    if (!q.sharp) { playScanSound("warning"); toast.warning("Document may be blurry — retake recommended."); markBlurry(src); }
    else { toast.success("Page captured"); }
  };
  const markBlurry = (src) => setPages((prev) => prev.map((p) => (p.src === src ? { ...p, blurry: true } : p)));
  // Native (iOS/Android) camera capture — reuses the SAME addPage pipeline. Inert on web (isNative=false).
  const captureNative = async () => {
    try {
      const file = await capturePhotoFile({ source: "camera" });
      if (!file) return;
      const r = new FileReader();
      r.onload = () => { addPage(r.result); playScanSound("capture"); haptic(20); toast.success("Page captured"); };
      r.readAsDataURL(file);
    } catch { toast.error("Camera unavailable. You can upload images instead."); }
  };

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => {
      if (!f.type.startsWith("image/")) return;
      const r = new FileReader();
      r.onload = () => addPage(r.result);
      r.readAsDataURL(f);
    });
    e.target.value = "";
  };

  const patchPage = (id, patch) => setPages((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const rotate = (id) => setPages((prev) => prev.map((p) => (p.id === id ? { ...p, rotation: (p.rotation + 90) % 360 } : p)));
  const removePage = (id) => setPages((prev) => { const n = prev.filter((p) => p.id !== id); if (activeId === id) setActiveId(n[0]?.id || null); return n; });
  const move = (id, dir) => setPages((prev) => {
    const i = prev.findIndex((p) => p.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= prev.length) return prev;
    const n = [...prev]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });

  const save = async () => {
    if (!pages.length) { toast.info("Capture or add at least one page first."); return; }
    setSaving(true);
    const done = [];
    const step = (stage) => setProc({ stage, done: [...done] });
    try {
      primeScanAudio();
      step("Enhancing document");
      const canvases = await Promise.all(pages.map((p) => renderPageToCanvas(p)));
      done.push("Enhancing document");
      if (pages.some((p) => p.original)) { step("Correcting perspective"); done.push("Correcting perspective"); }
      step("Building secure PDF");
      let blob;
      let searchable = false;
      if (flags.OCR_ENABLED) {
        try {
          const pageDataUrls = canvases.map((c) => c.toDataURL("image/jpeg", 0.82));
          const { data: built } = await api.post("/scan/build", { pages: pageDataUrls, compress: "medium" });
          const b64 = built.pdf.split(",", 2)[1];
          const bin = atob(b64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          blob = new Blob([bytes], { type: "application/pdf" });
          searchable = true;
        } catch (err) { console.error("Searchable PDF build failed, using image-only PDF:", err); }
      }
      if (!blob) {
        const first = canvases[0];
        const pdf = new jsPDF({ orientation: first.width >= first.height ? "l" : "p", unit: "px", format: [first.width, first.height], compress: true });
        pdf.addImage(first.toDataURL("image/jpeg", 0.82), "JPEG", 0, 0, first.width, first.height);
        for (let i = 1; i < canvases.length; i++) {
          const c = canvases[i];
          pdf.addPage([c.width, c.height], c.width >= c.height ? "l" : "p");
          pdf.addImage(c.toDataURL("image/jpeg", 0.82), "JPEG", 0, 0, c.width, c.height);
        }
        blob = pdf.output("blob");
      }
      if (blob.size > 25 * 1024 * 1024) { toast.error("Scan exceeds the 25 MB limit. Try fewer pages or B&W mode."); setSaving(false); setProc(null); return; }
      done.push(searchable ? "Building searchable PDF (OCR)" : "Building secure PDF");
      step("Saving to your vault");
      const name = (docName.trim() || `Scan ${new Date().toLocaleString()}`) + ".pdf";
      const fd = new FormData();
      fd.append("file", new File([blob], name, { type: "application/pdf" }));
      fd.append("name", docName.trim() || `Scan ${new Date().toLocaleDateString()}`);
      fd.append("category", category);
      fd.append("tags", tags.trim() ? Array.from(new Set(["scanned", "duruscan", ...tags.split(",").map((t) => t.trim()).filter(Boolean)])).join(",") : "scanned,duruscan");
      if (folderId) fd.append("folder_id", folderId);
      if (orgId) fd.append("org_id", orgId);
      const { data } = await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      done.push("Saving to your vault");
      if (flags.OCR_ENABLED && !searchable) { step("Extracting text (OCR)"); api.post(`/documents/${data.doc_id}/ocr`).catch(() => {}); done.push("Extracting text (OCR)"); }
      step("Scan complete");
      playScanSound("complete"); haptic(24);
      stopCamera();
      loadQuota();
      await new Promise((r) => setTimeout(r, 550));
      setProc(null);
      setPages([]); setActiveId(null); setDocName(""); setTags(""); setFolderId("");
      setDone({ ...data, searchable });
    } catch (e) {
      setProc(null);
      toast.error(apiError(e.response?.data?.detail) || "Could not save the scan. Please try again.");
    } finally { setSaving(false); }
  };

  if (!flags.DURU_SCAN_ENABLED) {
    return (
      <div className="max-w-xl">
        <div className="flex items-center gap-2 mb-1"><Scan size={26} className="text-primary" weight="duotone" /><h1 className="text-3xl font-bold tracking-tight">DuruScan</h1></div>
        <div className="mt-6 rounded-2xl border border-dashed border-white/20 bg-white p-8 text-center" data-testid="duruscan-disabled">
          <p className="text-white/85">DuruScan is currently turned off for this workspace.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl" data-testid="duruscan-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2"><Scan size={26} className="text-primary" weight="duotone" /><h1 className="text-3xl font-bold tracking-tight">DuruScan</h1></div>
          <p className="text-white/70 mt-1 text-sm">Capture, enhance and save documents straight into your vault — then OCR, sign, verify and share.</p>
        </div>
        <div className="text-sm text-white/60" data-testid="duruscan-page-count">{pages.length} page{pages.length !== 1 ? "s" : ""}</div>
      </div>

      <WorkflowSteps testId="scan-workflow" className="mb-6" steps={[
        { label: "Capture", icon: Camera },
        { label: "Enhance", icon: MagicWand },
        { label: "Build PDF", icon: FilePdf },
        { label: "Save to vault", icon: CheckCircle },
      ]} />

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Capture panel */}
        <div className="rounded-2xl border border-white/15 bg-white p-4">
          <div className="aspect-[4/3] rounded-xl overflow-hidden bg-black/60 grid place-items-center relative">
            {cameraOn
              ? <>
                  <video ref={videoRef} playsInline muted className="w-full h-full object-cover" data-testid="duruscan-video" />
                  <div className="duruscan-frame" aria-hidden="true" data-testid="duruscan-frame">
                    <span className="duruscan-corner tl" /><span className="duruscan-corner tr" />
                    <span className="duruscan-corner bl" /><span className="duruscan-corner br" />
                    <span className="duruscan-line" />
                  </div>
                  {liveQuad && (
                    <svg viewBox={`0 0 ${liveQuad.vw} ${liveQuad.vh}`} preserveAspectRatio="xMidYMid slice" className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true" data-testid="duruscan-live-edges">
                      <polygon points={liveQuad.corners.map(([x, y]) => `${x * liveQuad.vw},${y * liveQuad.vh}`).join(" ")} fill="rgba(201,165,92,0.14)" stroke="#c9a55c" strokeWidth={Math.max(2, liveQuad.vw * 0.004)} strokeLinejoin="round" />
                    </svg>
                  )}
                  {captureFlash && <span className="duruscan-flash" aria-hidden="true" />}
                </>
              : <div className="text-center text-white/50 px-6"><Camera size={40} className="mx-auto mb-2 opacity-70" /><p className="text-sm">Use your camera or upload images to start a scan.</p></div>}
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {!cameraOn
              ? (isNative
                  ? <Button onClick={captureNative} className="rounded-full" data-testid="duruscan-native-capture"><Camera size={18} className="mr-1.5" /> Capture with camera</Button>
                  : <Button onClick={startCamera} className="rounded-full" data-testid="duruscan-start-camera"><Camera size={18} className="mr-1.5" /> Open camera</Button>)
              : <>
                  <Button onClick={capture} className="rounded-full" data-testid="duruscan-capture"><Scan size={18} className="mr-1.5" /> Capture page</Button>
                  <Button variant="outline" onClick={stopCamera} data-testid="duruscan-stop-camera"><StopCircle size={18} className="mr-1.5" /> Stop</Button>
                </>}
            <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="duruscan-upload"><UploadSimple size={18} className="mr-1.5" /> Add image(s)</Button>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onFiles} data-testid="duruscan-file-input" />
          </div>

          {/* Enhancement controls for the active page */}
          {active && (
            <div className="mt-4 border-t border-white/10 pt-4" data-testid="duruscan-enhance">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-white/85">Enhance page</span>
                <div className="flex gap-1.5">
                  {active.original && <Button size="sm" variant="ghost" className="text-white/60" onClick={undoCrop} data-testid="duruscan-undo-crop">Undo</Button>}
                  <Button size="sm" variant="outline" onClick={autoEnhance} disabled={enhancing} data-testid="duruscan-auto-enhance">{enhancing ? <CircleNotch size={15} className="mr-1 animate-spin" /> : <MagicWand size={15} className="mr-1" />} Auto enhance</Button>
                  <Button size="sm" variant="outline" onClick={autoCrop} disabled={cropping} data-testid="duruscan-autocrop">{cropping ? <CircleNotch size={15} className="mr-1 animate-spin" /> : <Crop size={15} className="mr-1" />} Auto-crop</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditCorners(true)} data-testid="duruscan-adjust-corners"><Scan size={15} className="mr-1" /> Adjust corners</Button>
                  <Button size="sm" variant="outline" onClick={() => rotate(active.id)} data-testid="duruscan-rotate"><ArrowClockwise size={15} className="mr-1" /> Rotate</Button>
                </div>
              </div>
              <div className="flex gap-1.5 mb-3">
                {MODES.map(([m, label]) => (
                  <button key={m} onClick={() => patchPage(active.id, { mode: m })} data-testid={`duruscan-mode-${m}`}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${active.mode === m ? "bg-primary text-[#20242a] border-primary font-medium" : "border-white/20 text-white/80 hover:bg-white/10"}`}>{label}</button>
                ))}
              </div>
              <label className="text-xs text-white/60 flex items-center justify-between">Brightness <span>{active.brightness}%</span></label>
              <input type="range" min="60" max="160" value={active.brightness} onChange={(e) => patchPage(active.id, { brightness: +e.target.value })} className="w-full accent-primary" data-testid="duruscan-brightness" />
              <label className="text-xs text-white/60 flex items-center justify-between mt-2">Contrast <span>{active.contrast}%</span></label>
              <input type="range" min="60" max="180" value={active.contrast} onChange={(e) => patchPage(active.id, { contrast: +e.target.value })} className="w-full accent-primary" data-testid="duruscan-contrast" />
            </div>
          )}
        </div>

        {/* Pages panel */}
        <div className="rounded-2xl border border-white/15 bg-white p-4">
          {pages.length > 0 && (
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-white/85">Pages</span>
              <Button size="sm" variant="outline" onClick={autoCropAll} disabled={cropping} data-testid="duruscan-autocrop-all">
                {cropping ? <CircleNotch size={15} className="mr-1 animate-spin" /> : <MagicWand size={15} className="mr-1" />} Auto-crop &amp; clean all
              </Button>
            </div>
          )}
          {pages.length === 0
            ? <div className="h-full min-h-[240px] grid place-items-center text-white/50 text-sm">No pages yet. Captured and uploaded pages appear here.</div>
            : <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="duruscan-pages">
                {pages.map((p, i) => (
                  <div key={p.id} onClick={() => setActiveId(p.id)} data-testid={`duruscan-thumb-${i}`}
                    className={`relative rounded-xl overflow-hidden border-2 cursor-pointer bg-black/40 ${activeId === p.id ? "border-primary" : "border-white/10"}`}>
                    <div className="aspect-[3/4] overflow-hidden grid place-items-center">
                      <img src={p.src} alt={`Page ${i + 1}`} className="w-full h-full object-cover" style={pageStyle(p)} />
                    </div>
                    <span className="absolute top-1 left-1 text-[10px] bg-black/70 text-white rounded px-1.5 py-0.5">{i + 1}</span>
                    {p.blurry && <span className="absolute top-1 right-1 text-[9px] bg-amber-500 text-black font-medium rounded px-1.5 py-0.5" data-testid={`duruscan-blurry-${i}`}>Blurry?</span>}
                    <div className="absolute bottom-1 right-1 flex gap-1">
                      <button onClick={(e) => { e.stopPropagation(); move(p.id, -1); }} className="w-6 h-6 grid place-items-center rounded bg-black/70 text-white hover:bg-primary hover:text-[#20242a]" data-testid={`duruscan-up-${i}`}><ArrowUp size={12} /></button>
                      <button onClick={(e) => { e.stopPropagation(); move(p.id, 1); }} className="w-6 h-6 grid place-items-center rounded bg-black/70 text-white hover:bg-primary hover:text-[#20242a]" data-testid={`duruscan-down-${i}`}><ArrowDown size={12} /></button>
                      <button onClick={(e) => { e.stopPropagation(); removePage(p.id); }} className="w-6 h-6 grid place-items-center rounded bg-black/70 text-white hover:bg-red-500" data-testid={`duruscan-delete-${i}`}><Trash size={12} /></button>
                    </div>
                  </div>
                ))}
              </div>}
        </div>
      </div>

      {editCorners && active && (
        <CornerEditor src={active.src} onClose={() => setEditCorners(false)}
          onApply={(img) => { patchPage(active.id, { src: img, original: active.original || active.src, rotation: 0 }); setEditCorners(false); toast.success("Page straightened"); }} />
      )}

      {/* Save bar */}
      <div className="mt-6 rounded-2xl border border-white/15 bg-white p-4 flex flex-wrap items-end gap-3" data-testid="duruscan-savebar">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs text-white/60">Document name</Label>
          <Input value={docName} onChange={(e) => setDocName(e.target.value)} placeholder="e.g. Signed lease agreement" className="mt-1 bg-white/5 border-white/20 text-white placeholder:text-white/40" data-testid="duruscan-name" />
        </div>
        <div className="w-[180px]">
          <Label className="text-xs text-white/60">Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="mt-1" data-testid="duruscan-category"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="w-[180px]">
          <Label className="text-xs text-white/60">Folder</Label>
          <Select value={folderId || "none"} onValueChange={(v) => setFolderId(v === "none" ? "" : v)}>
            <SelectTrigger className="mt-1" data-testid="duruscan-folder"><SelectValue placeholder="No folder" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No folder</SelectItem>
              {folders.map((f) => <SelectItem key={f.folder_id} value={f.folder_id}>{f.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-[200px]">
          <Label className="text-xs text-white/60">Tags (comma-separated)</Label>
          <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="e.g. tax, 2026" className="mt-1 bg-white/5 border-white/20 text-white placeholder:text-white/40" data-testid="duruscan-tags" />
        </div>
        <Button variant="outline" onClick={suggest} disabled={suggesting || !pages.length} data-testid="duruscan-suggest">
          {suggesting ? <CircleNotch size={16} className="mr-1.5 animate-spin" /> : <MagicWand size={16} className="mr-1.5" />} Suggest name &amp; type
        </Button>
        <Button onClick={save} disabled={saving || !pages.length} className="rounded-full px-6" data-testid="duruscan-save">
          {saving ? <><CircleNotch size={18} className="mr-1.5 animate-spin" /> Generating…</> : <><FilePdf size={18} className="mr-1.5" /> Generate & save PDF</>}
        </Button>
      </div>
      {quota && (
        <p className="mt-2 text-xs text-white/50" data-testid="duruscan-quota">
          Scans this month: {quota.scan.used}{quota.scan.limit == null ? " (unlimited)" : ` / ${quota.scan.limit}`}
          {" · "}OCR: {quota.ocr.used}{quota.ocr.limit == null ? " (unlimited)" : ` / ${quota.ocr.limit}`}
          {" · "}<span className="uppercase">{quota.plan_id}</span> plan
        </p>
      )}

      {/* Futuristic processing sequence — each step reflects real work being done */}
      {proc && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4" data-testid="duruscan-processing">
          <div className="w-full max-w-sm rounded-2xl bg-[#0b0f17] text-white ring-1 ring-white/10 overflow-hidden" style={{ "--duruscan-accent": "#c9a34e" }}>
            <div className="relative h-28 grid place-items-center overflow-hidden border-b border-white/10">
              <div className="duruscan-frame"><span className="duruscan-line" /></div>
              <Scan size={34} className="text-primary" weight="duotone" />
            </div>
            <div className="p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-primary flex items-center gap-1.5"><Scan size={13} /> DuruScan</p>
              <p className="text-lg font-semibold mt-1" aria-live="polite" data-testid="duruscan-proc-stage">{proc.stage}</p>
              <div className="mt-3 space-y-1.5">
                {proc.done.map((d) => (
                  <div key={d} className="flex items-center gap-2 text-xs text-white/60"><CheckCircle size={14} weight="fill" className="text-emerald-400" /> {d}</div>
                ))}
                {proc.stage !== "Scan complete" && (
                  <div className="flex items-center gap-2 text-xs text-white/90"><CircleNotch size={14} className="animate-spin text-primary" /> {proc.stage}…</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success / next-steps */}
      {done && (
        <Dialog open onOpenChange={(o) => { if (!o) { setDone(null); navigate("/dashboard/vault"); } }}>
          <DialogContent className="max-w-md" data-testid="duruscan-done-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><CheckCircle size={22} weight="fill" className="text-emerald-500" /> Saved to your vault</DialogTitle>
              <DialogDescription>"{done.name}" is now a normal DuruVault document{done.searchable ? " — with a searchable OCR text layer, so you can select and search its text." : (flags.OCR_ENABLED ? " — OCR is running in the background so it becomes searchable." : ".")}</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 gap-2">
              <Button variant="outline" onClick={() => navigate(`/dashboard/vault?open=${done.doc_id}`)} data-testid="duruscan-open-vault"><TextAa size={16} className="mr-2" /> Open in Vault</Button>
              {flags.DURU_SIGN_ENABLED && <Button variant="outline" onClick={() => navigate("/dashboard/sign")} data-testid="duruscan-goto-sign"><PenNib size={16} className="mr-2" /> Send for signature</Button>}
              {flags.DURU_TWIN_ENABLED && <Button variant="outline" onClick={() => navigate(`/dashboard/twin/${done.doc_id}`)} data-testid="duruscan-goto-twin"><MagicWand size={16} className="mr-2" /> Analyze with DuruTwin</Button>}
              {flags.DURU_VERIFY_ENABLED && <Button variant="outline" onClick={() => navigate("/dashboard/verify")} data-testid="duruscan-goto-verify"><ShieldStar size={16} className="mr-2" /> Verify document</Button>}
              {flags.DURU_SHIELD_ENABLED && <Button variant="outline" onClick={() => navigate("/dashboard/vault?open=" + done.doc_id)} data-testid="duruscan-goto-share"><ShieldStar size={16} className="mr-2" /> Protect & share</Button>}
            </div>
            <DialogFooter>
              <Button onClick={() => { setPages([]); setActiveId(null); setDocName(""); setDone(null); }} data-testid="duruscan-new">Scan another</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
